import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, Calendar, User } from "lucide-react";
import { useTranslation } from "react-i18next";

export const ARTICLES = [
  {
    id: "1",
    title: "Getting Started with Mogzit",
    category: "Getting Started",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "Welcome to Mogzit ethiopia's professional caregiver platform. Once you register and submit your application, our hr team will review your profile and initiate the vetting process. You will be notified at each step of the process via your registered email or phone number. After passing all vetting stages you will be marked as Onboarded and can start receiving bookings and job offers.",
    body: [
      {
        heading: "Step 1 Complete your registration",
        text: "Go to the registration page, fill in your personal details, service category, experience, and monthly fee. Make sure your information is accurate. it will be reviewed by the admin team.",
      },
      {
        heading: "Step 2 Submit required documents",
        text: "Upload your National ID or Kebele ID, an up to date passport photo, and any relevant certifications or diplomas. The platform requires these for identity verification.",
      },
      {
        heading: "Step 3 Wait for vetting",
        text: "Once your documents are submitted, the Mogzit admin team will contact you to schedule a medical test, interview, and final assessment. This process usually takes 5–10 business days.",
      },
      {
        heading: "Step 4 Get onboarded",
        text: "After passing all stages you will receive an Onboarded status and gain full access to the platform including bookings, job posts, contracts, and payments.",
      },
    ],
  },
  {
    id: "2",
    title: "How Salary Payments Work",
    category: "Payments",
    date: "Jul 01, 2026",
    author: "Mogzit Finance Team",
    content:
      "Your caregiver share is 80% of the amount remaining after Chapa's 2.875% processing fee.",
    body: [
      {
        heading: "Gross salary",
        text: "This is the agreed monthly salary shown on your contract. For example, if your client pays Br 5,000 per month, that is your gross amount.",
      },
      {
        heading: "Step 1 Payment gateway fee (2.875%)",
        text: "Chapa (our payment processor) charges 2.5% on every transaction. This is deducted first: Gross × 0.025. Example: 5,000 × 0.025 = Br 125.",
      },
      {
        heading: "Step 2 Platform service fee (20%)",
        text: "Mogzit deducts 20% from the remaining amount as a platform service fee: (Gross − gateway fee) × 0.20. Example: (5,000 − 125) × 0.20 = Br 975.",
      },
      {
        heading: "Your caregiver share (80% after Chapa)",
        text: "What you receive = Gross × 0.97125 × 0.80. The Payments, Dashboard, Bookings, and Contracts pages show this caregiver share.",
      },
    ],
  },
  {
    id: "3",
    title: "How to Apply for a Job Post",
    category: "Jobs",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "Clients and our hr team post job openings on the Mogzit platform. As a caregiver you can browse available jobs and submit an application directly through the portal.",
    body: [
      {
        heading: "Finding available jobs",
        text: "Go to Jobs in the sidebar. You will see a list of open job posts filtered by your service category. Each post shows the service type, location (sub-city), expected start date, and salary range.",
      },
      {
        heading: "Submitting your application",
        text: "Click on a job post to see the full details, then click Apply. Your profile information and availability are submitted automatically no extra form is required.",
      },
      {
        heading: "Tracking your application",
        text: "Once you apply, you can track the status under My Applications. Possible statuses are: Pending, Shortlisted, Accepted, or Rejected. You will be notified if the admin takes action.",
      },
      {
        heading: "Withdrawing an application",
        text: "If you change your mind before a decision is made, open the application and click Withdraw. You cannot withdraw after you have been Accepted.",
      },
    ],
  },
  {
    id: "4",
    title: "How to Edit Your Profile",
    category: "Account",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "Your profile is your professional identity on Mogzit. Keeping it up to date helps clients and the admin team understand your skills and availability.",
    body: [
      {
        heading: "Opening your profile",
        text: "Click Profile in the sidebar or tap your avatar at the top of the page. Your profile has three tabs: Profile, Security, and Bank & Payment.",
      },
      {
        heading: "Editing personal details",
        text: "On the Profile tab click Edit Profile. You can update your bio (About section), date of birth, gender, languages spoken, and availability status. Your name, email, and phone number are locked for security contact support to change them.",
      },
      {
        heading: "Changing your availability",
        text: "Toggle your availability to Yes or No directly from the Profile edit form. Setting it to No means new clients will not be matched to you until you turn it back on.",
      },
      {
        heading: "Setting up your bank account",
        text: "Go to the Bank & Payment tab. Enter your bank name and account number so Mogzit can send your monthly payout via Chapa. This must be completed before you can receive payments.",
      },
    ],
  },
  {
    id: "5",
    title: "How to Cancel a Booking",
    category: "Bookings",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "If you are unable to fulfil a booking you can cancel it directly from the Bookings page. Please cancel as early as possible so the client can be reassigned.",
    body: [
      {
        heading: "Cancelling a booking",
        text: "Go to Bookings in the sidebar. Find the booking you wish to cancel (it must be in New or In Progress status). Click the three-dot action menu on the right and select Cancel Booking.",
      },
      {
        heading: "Providing a reason",
        text: "You must enter a cancellation reason before confirming. Be honest and clear this is recorded and reviewed by the hr team.",
      },
      {
        heading: "After cancellation",
        text: "The booking status will change to Cancelled and the client and hr team will be notified automatically. Repeated cancellations without valid reasons may affect your standing on the platform.",
      },
      {
        heading: "Work-Started bookings",
        text: "Once a booking has moved to Work Started status it can only be completed, not cancelled. Contact the Mogzit hr team if you have an urgent situation.",
      },
    ],
  },
  {
    id: "6",
    title: "How to Change Language & Logout",
    category: "Account",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "The Mogzit caregiver portal supports English, Amharic, Afaan Oromoo, and Tigrigna. You can switch the language at any time and log out from the top navigation bar.",
    body: [
      {
        heading: "Switching language",
        text: "Click the language selector icon (globe icon) in the top navigation bar. Select English, Amharic, Afaan Oromoo, and Tigrigna. The entire interface will switch immediately without requiring a page reload.",
      },
      {
        heading: "Logging out",
        text: "Click your avatar or name in the top-right corner of the navigation bar, then click Logout. You will be redirected to the login page and your session will be cleared.",
      },
      {
        heading: "Staying logged in",
        text: "Your session remains active for up to 7 days if you do not log out. For security, always log out if you are using a shared or public device.",
      },
    ],
  },
  {
    id: "7",
    title: "Required Documents for Registration",
    category: "Vetting",
    date: "Jul 01, 2026",
    author: "Mogzit Vetting Team",
    content:
      "Before you can be onboarded as a Mogzit caregiver you must submit the following documents for identity and professional verification.",
    body: [
      {
        heading: "Mandatory documents",
        text: "1. National ID card or Kebele ID (both sides). 2. Recent passport-sized photograph (clear background, taken within the last 6 months). 3. Educational certificates or relevant training diplomas (e.g. nursing, childcare, elderly care).",
      },
      {
        heading: "Health documents",
        text: "A medical clearance certificate is required as part of the vetting process. Our team will direct you to an approved clinic if you do not have one.",
      },
      {
        heading: "Optional but recommended",
        text: "Reference letters from previous employers or community leaders, and any additional professional certifications (first aid, CPR, etc.) will strengthen your application.",
      },
      {
        heading: "How to submit",
        text: "Upload your documents when completing your registration, or bring them to our office at Global lancha, YeshiTam Building, 2nd Floor, Kirkos Sub-City, Addis Ababa. Document copies must be clear and legible; blurry or cropped images will be rejected.",
      },
    ],
  },
  {
    id: "8",
    title: "The Vetting Process — Step by Step",
    category: "Vetting",
    date: "Jul 01, 2026",
    author: "Mogzit Vetting Team",
    content:
      "Mogzit follows a rigorous multi-stage vetting process to ensure that all caregivers on the platform are trustworthy, qualified, and safe for families to hire.",
    body: [
      {
        heading: "Stage 1 — Application Submitted",
        text: "You complete your registration and submit your documents. The admin team will review and verify your information within 2–3 business days.",
      },
      {
        heading: "Stage 2 — Document Verification",
        text: "Your ID and certificates are verified. If anything is missing or unclear you will be contacted to resubmit.",
      },
      {
        heading: "Stage 3 — Medical Test",
        text: "You will be directed to an approved clinic for a standard health check. Results are reviewed by our team.",
      },
      {
        heading: "Stage 4 — Interview",
        text: "A Mogzit team member will schedule a brief interview (in person or by phone) to understand your experience, motivations, and working style.",
      },
      {
        heading: "Stage 5 — Final Assessment / Exam",
        text: "A practical or written assessment tests your relevant caregiver skills (childcare, elderly care, etc.).",
      },
      {
        heading: "Onboarded",
        text: "Once all stages are passed your status becomes Onboarded and you gain full platform access. If rejected at any stage, the admin team will inform you with a reason.",
      },
    ],
  },
  {
    id: "9",
    title: "Understanding Your Dashboard",
    category: "Getting Started",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "Your dashboard is the first page you see when you log in. It gives you a quick overview of your profile stats, active contract, and recent payouts.",
    body: [
      {
        heading: "Stats cards",
        text: "The four cards at the top show: your Rating, total completed bookings, your availability status, and your estimated monthly salary (after platform deductions).",
      },
      {
        heading: "Active contract card",
        text: "If you have an active monthly contract this section shows the client name, your net monthly salary, start date, and the date of the next payout.",
      },
      {
        heading: "Recent payouts",
        text: "The last 3 payouts are listed here with the period (e.g. July 2026) and your net amount received. Full payout history is available on the Payments page.",
      },
      {
        heading: "Quick links",
        text: "The bottom section provides shortcuts to your bookings, jobs, contracts, and profile so you can navigate quickly.",
      },
    ],
  },
  {
    id: "10",
    title: "How to Change Your Password",
    category: "Account",
    date: "Jul 01, 2026",
    author: "Mogzit Support Team",
    content:
      "You can change your password at any time from the Security tab in your Profile. Make sure to choose a strong password that you have not used before.",
    body: [
      {
        heading: "Changing your password",
        text: "Go to Profile → Security tab. Enter your current password, then type your new password and confirm it. The new password must be at least 8 characters long.",
      },
      {
        heading: "Forgot your password?",
        text: "On the login page click Forgot Password. Enter your registered email address and you will receive a reset link within a few minutes. Check your spam folder if you do not see it.",
      },
      {
        heading: "Tips for a strong password",
        text: "Use a mix of uppercase and lowercase letters, numbers, and symbols. Avoid using your name or date of birth. Do not share your password with anyone.",
      },
    ],
  },
];

export const getLocalizedArticles = (t) => {
  const articles = [];
  for (let i = 1; i <= 10; i++) {
    const body = [];
    for (let s = 1; s <= 6; s++) {
      const headingKey = `helpArticles.articles.${i}.s${s}h`;
      const textKey = `helpArticles.articles.${i}.s${s}t`;
      const headingVal = t(headingKey);
      const textVal = t(textKey);
      if (
        headingVal &&
        !headingVal.startsWith("helpArticles.articles.") &&
        textVal &&
        !textVal.startsWith("helpArticles.articles.")
      ) {
        body.push({
          heading: headingVal,
          text: textVal,
        });
      }
    }
    articles.push({
      id: String(i),
      title: t(`helpArticles.articles.${i}.title`),
      category: t(`helpArticles.articles.${i}.category`),
      author: t(`helpArticles.articles.${i}.author`),
      content: t(`helpArticles.articles.${i}.content`),
      date: "Jul 01, 2026",
      body,
    });
  }
  return articles;
};

const CareGiverArticle = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const articles = getLocalizedArticles(t);
  const article = articles.find((a) => a.id === id);

  if (!article)
    return (
      <div className="p-8 text-center flex flex-col items-center justify-center h-[50vh]">
        <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-2">
          {t("helpArticles.notFound")}
        </h2>
        <button
          onClick={() => navigate("/caregiver/help")}
          className="text-primary hover:underline"
        >
          {t("helpArticles.backLink")}
        </button>
      </div>
    );

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-4xl mx-auto pb-12 p-4">
      <button
        onClick={() => navigate("/caregiver/help")}
        className="flex items-center gap-2 text-gray-500 hover:text-text dark:text-dark-muted dark:hover:text-dark-text mb-6 transition-colors font-medium"
      >
        <ChevronLeft size={20} /> {t("helpArticles.backToHelp")}
      </button>

      <div className="bg-card dark:bg-dark-surface rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 dark:border-dark-border">
        <span className="px-3 py-1 bg-primary text-white rounded-full text-xs font-bold uppercase tracking-wide mb-6 inline-block">
          {article.category}
        </span>
        <h1 className="text-3xl md:text-4xl font-extrabold text-text dark:text-dark-text mb-6 leading-tight">
          {article.title}
        </h1>

        <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-dark-muted mb-8 border-b border-gray-100 dark:border-dark-border pb-8">
          <div className="flex items-center gap-2">
            <User size={16} /> <span>{article.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={16} /> <span>{article.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>{t("helpArticles.minRead")}</span>
          </div>
        </div>

        <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-dark-muted leading-loose space-y-6">
          <p className="text-base leading-relaxed">{article.content}</p>
          {article.body?.map((section, i) => (
            <div key={i}>
              <h3 className="text-lg font-bold text-text dark:text-dark-text mt-6 mb-2">
                {section.heading}
              </h3>
              <p className="text-gray-600 dark:text-dark-muted leading-relaxed">
                {section.text}
              </p>
            </div>
          ))}
          <p className="mt-8 text-sm text-gray-500 dark:text-dark-muted border-t border-gray-100 dark:border-dark-border pt-6 whitespace-pre-line">
            {t("helpArticles.contactFooter")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CareGiverArticle;
